import { policyResultKeys } from './policy-order-api'
import { decodePolicyMutation, decodePolicyRefreshStatus, type PolicyRefreshStatus } from '../lib/policy-authoring'
import type { components } from '@/lib/openapi.generated'
import {
  isRecord,
  json,
  nullableNumber,
  nullableString,
  request,
  requireBoolean,
  requireNumber,
  requireString,
  requireTimestamp,
  withQuery,
} from '@/lib/api'

type Schemas = components['schemas']
export const assistantPolicyKeys = {
  result: (entityId: string) => policyResultKeys.type(entityId, ASSISTANT_POLICY_TYPE),
  own: (entityId: string) => [...policyResultKeys.type(entityId, ASSISTANT_POLICY_TYPE), 'own'] as const,
}
export const ASSISTANT_POLICY_TYPE = 'entity:ai.assistant.enabled'
export type AssistantDefinition = Pick<Schemas['AssistantPolicyResponse'], 'id' | 'revision' | 'value'>
export type { PolicyRefreshStatus }
export type AssistantPolicyResult = Omit<
  Schemas['AssistantPolicyResultResponse'],
  'selectionStatus' | 'refreshStatus'
> & {
  selectionStatus: 'NeverEvaluated' | 'Evaluated'
  refreshStatus: PolicyRefreshStatus
}

function decodeDefinition(raw: unknown): AssistantDefinition {
  if (!isRecord(raw)) throw new Error('Malformed assistant policy')
  return {
    id: requireString(raw.id, 'id'),
    revision: requireNumber(raw.revision, 'revision'),
    value: requireBoolean(raw.value, 'value'),
  } satisfies AssistantDefinition
}

function decodeOwnDefinition(raw: unknown): AssistantDefinition | null {
  if (!isRecord(raw) || !Array.isArray(raw.items)) throw new Error('Malformed assistant policy list')
  if (raw.items.length > 1) throw new Error('Multiple assistant definitions need an administrator to finish the upgrade.')
  return raw.items.length === 0 ? null : decodeDefinition(raw.items[0])
}

function decodeSetResult(raw: unknown) {
  if (!isRecord(raw)) throw new Error('Malformed assistant settings result')
  return {
    enabled: requireBoolean(raw.enabled, 'enabled'),
    mutation: raw.mutation == null ? null : decodePolicyMutation(raw.mutation),
  }
}

function decodeResult(raw: unknown): AssistantPolicyResult {
  if (!isRecord(raw)) throw new Error('Malformed assistant policy result')
  if (raw.selectionStatus !== 'NeverEvaluated' && raw.selectionStatus !== 'Evaluated')
    throw new Error('Unexpected policy selection status')
  return {
    selectionStatus: raw.selectionStatus,
    selectionRevision: nullableNumber(raw.selectionRevision, 'selectionRevision'),
    evaluatedAt: raw.evaluatedAt == null ? null : requireTimestamp(raw.evaluatedAt, 'evaluatedAt'),
    refreshStatus: decodePolicyRefreshStatus(raw.refreshStatus),
    failureCode: nullableString(raw.failureCode, 'failureCode'),
    value: raw.value == null ? null : requireBoolean(raw.value, 'value'),
    isDefault: requireBoolean(raw.isDefault, 'isDefault'),
  } satisfies AssistantPolicyResult
}

const resultPath = (entityId: string) => `/api/v1/entities/${entityId}/assistant-policy`

export const assistantPolicyApi = {
  result: (entityId: string) => request(resultPath(entityId), decodeResult),
  own: (entityId: string) =>
    request(withQuery(`/api/v1/entities/${entityId}/assistant-policies`, { limit: '2' }), decodeOwnDefinition),
  set: (entityId: string, enabled: boolean, own: AssistantDefinition | null, operationId: string) =>
    request(
      `/api/v1/entities/${entityId}/assistant-settings`,
      decodeSetResult,
      json('PUT', {
        enabled,
        expectedDefinition: own ? { id: own.id, revision: own.revision } : null,
        operationId,
      } satisfies Schemas['SetAssistantSettingsRequest']),
    ),
}
